/**
 * Best Practice Auth Store
 * Single source of truth for authentication state
 * Tokens stored ONLY in memory (never localStorage)
 * Refresh tokens handled via httpOnly cookies
 */

interface AuthState {
  accessToken: string | null;
  user: {
    id: number | null;
    email: string | null;
    userType: string | null;
    firstname?: string;
    lastname?: string;
    enterpriseName?: string;
    telephone?: string;
    address?: string;
    city?: string;
    country?: string;
    ice?: string;
    identifiantFiscal?: string;
    siren?: string;
    siret?: string;
    companyNumber?: string;
    nif?: string;
    codiceFiscale?: string;
    uid?: string;
    steuerNumber?: string;
    rcsNumber?: string;
    kvkNumber?: string;
    defaultInvoiceLegalMentions?: string;
    vatNumber?: string;
    iban?: string;
    bankName?: string;
  } | null;
  isInitialized: boolean;
  isAuthenticated: boolean;
}

class AuthStore {
  private state: AuthState = {
    accessToken: null,
    user: null,
    isInitialized: false,
    isAuthenticated: false,
  };

  private listeners: Set<() => void> = new Set();

  // Subscribe to auth state changes
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // Notify all listeners
  private notify(): void {
    this.listeners.forEach(listener => listener());
  }

  // Get current state
  getState(): AuthState {
    return { ...this.state };
  }

  // Set access token (in memory only)
  setAccessToken(token: string | null): void {
    this.state.accessToken = token;
    this.state.isAuthenticated = !!token;
    this.notify();
  }

  // Set user info
  setUser(user: AuthState['user']): void {
    this.state.user = user;
    this.notify();
  }

  // Mark as initialized
  setInitialized(initialized: boolean): void {
    this.state.isInitialized = initialized;
    this.notify();
  }

  // Clear all auth state
  // Set isInitialized to true because we've determined the user is not authenticated
  clear(): void {
    this.state = {
      accessToken: null,
      user: null,
      isInitialized: true, // Mark as initialized - we know user is not authenticated
      isAuthenticated: false,
    };
    this.notify();
  }

  // Reset initialization state (for logout)
  reset(): void {
    this.state = {
      accessToken: null,
      user: null,
      isInitialized: false,
      isAuthenticated: false,
    };
    this.notify();
  }

  // Get access token
  getAccessToken(): string | null {
    return this.state.accessToken;
  }

  // Check if authenticated
  isAuthenticated(): boolean {
    return this.state.isAuthenticated && !!this.state.accessToken;
  }

  // Check if initialized
  isInitialized(): boolean {
    return this.state.isInitialized;
  }
}

// Singleton instance
export const authStore = new AuthStore();

